/**
 * Utilities for exporting parsed P&ID / PFD diagrams as canvas images.
 */

/** Strip the file extension from a document name so we can append .png without doubling. */
export function stripFileExtension(name: string): string {
  return name.includes('.') ? name.substring(0, name.lastIndexOf('.')) : name
}

/**
 * Draw a WKT geometry (LINESTRING or MULTILINESTRING) onto a canvas context.
 * The canvas context must already have strokeStyle and lineWidth set by the caller.
 */
export function drawWktOnCanvas(
  ctx: CanvasRenderingContext2D,
  wkt: string,
): void {
  if (wkt.startsWith('MULTILINESTRING')) {
    // Format: MULTILINESTRING ((x1 y1, x2 y2), (x3 y3, x4 y4), ...)
    // Extract the content inside the outer parentheses, then split on "), ("
    const inner = wkt.match(/MULTILINESTRING\s*\(\((.+)\)\)/s)
    if (!inner) return
    const segments = inner[1].split(/\)\s*,\s*\(/)
    for (const segment of segments) {
      drawCoordsOnCanvas(ctx, parseCoordString(segment))
    }
  } else if (wkt.includes('LINESTRING')) {
    // Format: LINESTRING (x1 y1, x2 y2, ...)
    const match = wkt.match(/LINESTRING\s*\(([^)]+)\)/)
    if (!match) return
    drawCoordsOnCanvas(ctx, parseCoordString(match[1]))
  }
}

function parseCoordString(str: string): { x: number; y: number }[] {
  return str
    .split(',')
    .map((pair) => {
      const [x, y] = pair.trim().split(/\s+/).map(Number)
      return { x, y }
    })
    .filter((p) => !isNaN(p.x) && !isNaN(p.y))
}

function drawCoordsOnCanvas(
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
): void {
  if (points.length < 2) return
  ctx.beginPath()
  ctx.moveTo(points[0].x, points[0].y)
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y)
  ctx.stroke()
}
